import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Play, Trash2, Ban, CheckCircle, MoreVertical } from "lucide-react";

import {
  useDeleteGoldenAuditDefinitionMutation,
  useRunGoldenAuditNowMutation,
  useUpdateGoldenAuditScheduleMutation,
} from "../../store/api/audit/goldenAuditApiSlice";
import { useStoreDispatch } from "../../store/hooks";
import { setOpenToast } from "../../store/helpers/helperReducers";
import { selectProfilePermissionsSelector } from "../../store/api/auth/selectors";
import ProfilePermissions from "../../utils/ProfilePermissions";
import { errorMessage } from "../../utils/helpers";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

interface IGoldenDefinitionActionsMenuProps {
  id: number;
  name?: string;
  scheduleEnabled?: boolean;
}

const GoldenDefinitionActionsMenu: React.FC<IGoldenDefinitionActionsMenuProps> = ({
  id,
  name,
  scheduleEnabled,
}) => {
  const dispatch = useStoreDispatch();
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteDefinition, { isLoading: deleteLoading }] = useDeleteGoldenAuditDefinitionMutation();
  const [runGoldenAuditNow, { isLoading: runNowLoading }] = useRunGoldenAuditNowMutation();
  const [updateSchedule, { isLoading: scheduleLoading }] = useUpdateGoldenAuditScheduleMutation();

  if (!permissions?.hasOpCodeGroup("manage_topology")) {
    return null;
  }

  const handleToggleSchedule = async () => {
    try {
      await updateSchedule({ id, scheduleEnabled: !scheduleEnabled }).unwrap();
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "success",
          message: scheduleEnabled ? "Audit definition disabled" : "Audit definition enabled",
        })
      );
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  const handleRunNow = async () => {
    try {
      await runGoldenAuditNow(id).unwrap();
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "success",
          message: "Audit run started.",
        })
      );
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteDefinition(id).unwrap();
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "success",
          message: name
            ? `Audit definition "${name}" has been deleted successfully`
            : "Audit definition has been deleted successfully",
        })
      );
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    } finally {
      setDeleteOpen(false);
    }
  };

  const toggleLabel = scheduleEnabled ? "Disable" : "Enable";

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="inline-flex items-center justify-center rounded-md text-sm h-8 w-8 bg-transparent transition-colors duration-150 hover:bg-accent"
            aria-label="golden definition actions"
          >
            <MoreVertical className="h-4 w-4 dark:text-white" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[140px] z-[1301]">
          {scheduleEnabled && (
            <DropdownMenuItem
              className="cursor-pointer"
              disabled={runNowLoading}
              onSelect={handleRunNow}
            >
              <Play className="h-4 w-4 mr-2" />
              Run now
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            className="cursor-pointer"
            disabled={scheduleLoading}
            onSelect={handleToggleSchedule}
          >
            {scheduleEnabled ? (
              <Ban className="h-4 w-4 mr-2" />
            ) : (
              <CheckCircle className="h-4 w-4 mr-2" />
            )}
            {toggleLabel}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer text-destructive focus:text-destructive"
            disabled={deleteLoading}
            onSelect={() => setDeleteOpen(true)}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete audit definition</AlertDialogTitle>
            <AlertDialogDescription>
              {name
                ? `Are you sure you want to delete the audit definition "${name}"? This will also remove its latest audit results and violations.`
                : "Are you sure you want to delete this audit definition? This will also remove its latest audit results and violations."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={deleteLoading} onClick={handleDeleteConfirm}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default GoldenDefinitionActionsMenu;
